import { type MedicalPlanFilters } from './hooks/useMedicalPlanFilters'

// Boolean filter keys that need string conversion for EnterpriseFiltersView
const BOOLEAN_FILTER_KEYS = new Set(['hsaEligible', 'offMarketOnly', 'activelyMarketedOnly'])

// Filters with default value of true (only appear in URL when false)
const DEFAULT_TRUE_FILTERS = new Set(['offMarketOnly', 'activelyMarketedOnly'])

// Filter keys that should allow empty strings/arrays (for newly added filters in UI)
// These are all non-boolean filters that can be added via the filter dropdown
export const FILTERS_ALLOWING_EMPTY_VALUES = new Set([
  'zipCode',
  'fipsCountyCode',
  'memberId',
  'hiosId',
  'medicalPlanId',
  'carrierNames',
  'metalTiers',
  'planTypes',
])

// Boolean filter keys that should default to undefined (not empty string in URL)
// This only includes hsaEligible because offMarketOnly and activelyMarketedOnly default to true
export const OPTIONAL_BOOLEAN_FILTER_KEYS = new Set(['hsaEligible'])

/**
 * Check if a filter key corresponds to a boolean filter type
 */
export function isBooleanFilter (key: string): boolean {
  return BOOLEAN_FILTER_KEYS.has(key)
}

/**
 * Check if a filter value is considered "default" (empty or undefined)
 * For boolean filters with default true, the default value is true
 */
export function isDefaultValue (key: string, value: unknown): boolean {
  if (value === undefined || value === null) return true
  if (value === '') return true
  if (Array.isArray(value) && value.length === 0) return true

  // For boolean filters that default to true, true is the default value
  if (DEFAULT_TRUE_FILTERS.has(key) && value === true) return true

  return false
}

/**
 * Convert a boolean filter value to string format for EnterpriseFiltersView
 */
function booleanToString (value: boolean | string | undefined): string {
  if (value === true || value === false) {
    return String(value)
  }
  // Keep empty string for newly added filters
  return ''
}

/**
 * Convert a string filter value back to boolean
 */
function stringToBoolean (value: string | undefined): boolean | '' | undefined {
  if (value === '') return ''
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}

/**
 * Convert filters to format expected by EnterpriseFiltersView
 * (booleans need to be strings for single-select filters)
 */
export function toEnterpriseFormat (
  filters: Partial<MedicalPlanFilters>
): Record<string, unknown> {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (isBooleanFilter(key)) {
      acc[key] = booleanToString(value as boolean | string | undefined)
    } else {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, unknown>)
}

/**
 * Convert filters from EnterpriseFiltersView format back to MedicalPlanFilters format
 * (strings need to be converted back to booleans)
 */
export function fromEnterpriseFormat (
  filters: Record<string, unknown>
): Partial<MedicalPlanFilters> {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (isBooleanFilter(key)) {
      const converted = stringToBoolean(value as string | undefined)
      if (converted !== undefined) {
        acc[key as keyof MedicalPlanFilters] = converted as any
      }
    } else {
      acc[key as keyof MedicalPlanFilters] = value as any
    }
    return acc
  }, {} as Partial<MedicalPlanFilters>)
}

/**
 * Build displayed filters by including:
 * 1. Filters that are visible (in visibleFilters set)
 * 2. Filters that have non-default values
 *
 * For visible filters without values, use appropriate placeholder values
 */
export function buildDisplayedFilters (
  filters: MedicalPlanFilters,
  visibleFilters: Set<string>,
  filterConfigurations: Record<string, { isMultiValue: boolean }>
): Partial<MedicalPlanFilters> {
  const result: Partial<MedicalPlanFilters> = {}

  // Get all possible filter keys from configurations
  const allFilterKeys = Object.keys(filterConfigurations)

  allFilterKeys.forEach((key) => {
    const filterKey = key as keyof MedicalPlanFilters
    const filterValue = filters[filterKey]
    const isVisible = visibleFilters.has(key)
    const hasValue = !isDefaultValue(key, filterValue)

    // Include filter if it's visible OR has a value
    if (isVisible || hasValue) {
      if (isVisible && !hasValue) {
        // Use placeholder for visible filters without values
        if (isBooleanFilter(key)) {
          result[filterKey] = '' as any
        } else if (filterConfigurations[key].isMultiValue) {
          result[filterKey] = [] as any
        } else {
          result[filterKey] = '' as any
        }
      } else {
        // Use actual value
        result[filterKey] = filterValue as any
      }
    }
  })

  return result
}

/**
 * Update visible filters set based on changes
 * - Add filters that are in the updated set
 * - Remove filters that were removed (exist in current but not in updated)
 */
export function updateVisibleFilters (
  currentFilters: Record<string, unknown>,
  updatedFilters: Record<string, unknown>,
  visibleFilters: Set<string>,
  filterConfigurations: Record<string, unknown>
): Set<string> {
  const next = new Set(visibleFilters)

  // Add any new filters
  Object.keys(updatedFilters).forEach((key) => {
    if (filterConfigurations[key]) {
      next.add(key)
    }
  })

  // Remove filters that were removed
  Object.keys(currentFilters).forEach((key) => {
    if (!(key in updatedFilters)) {
      next.delete(key)
    }
  })

  return next
}

/**
 * Merge updated filters with existing filters, handling removals and updates
 */
export function mergeFilterUpdates (
  baseFilters: MedicalPlanFilters,
  currentDisplayed: Record<string, unknown>,
  updatedFilters: Record<string, unknown>,
  filterConfigurations: Record<string, unknown>
): MedicalPlanFilters {
  const merged: MedicalPlanFilters = { ...baseFilters }

  // Handle removed filters (exist in current but not in updated)
  Object.keys(filterConfigurations).forEach((key) => {
    const filterKey = key as keyof MedicalPlanFilters
    if (key in currentDisplayed && !(key in updatedFilters)) {
      merged[filterKey] = undefined as any
    }
  })

  // Convert updated filters from enterprise format and merge
  const convertedUpdates = fromEnterpriseFormat(updatedFilters)

  Object.keys(convertedUpdates).forEach((key) => {
    const filterKey = key as keyof MedicalPlanFilters
    if (filterConfigurations[key]) {
      const value = convertedUpdates[filterKey]
      merged[filterKey] = value as any
    }
  })

  return merged
}
